/**
 * Experiment-assignment rail (WS-F) — deterministic, sticky-by-construction
 * holdout arms for any offer/surface lever, read on SERVER truth.
 *
 * Design (why past lever readouts were confounded and this isn't):
 *   - Assignment is a pure hash of (experimentId, clientId) — no storage write,
 *     no flicker, stable across renders/sessions/devices sharing the browser id.
 *     Buckets are independent across experiments (the id is part of the seed).
 *   - Exposure (the `experiment_exposed` owned-beacon event — the SAME stream
 *     the ADR-0034 loop writes) is emitted ONLY at the decision point where the
 *     arms' behavior diverges, once per session per experiment — never per page
 *     view (the part everyone gets wrong; a diluted exposure denominator hides
 *     real effects).
 *   - The arm reaches revenue with ZERO client-session joins: `createPaymentOrder`
 *     stamps `experiments: "expId:arm,..."` into Razorpay order notes
 *     (`lib/api/plans.ts` → backend `orders.py` → razorpay_payments.notes_json),
 *     so `execution/experiment_readout.py` splits ₹/orders by arm from the lake.
 *
 * Relationship to the EXISTING experiment code (read before adding either kind):
 *   - `lib/ab.ts` — the 2-arm 50/50 hash primitive (djb2). Kept as-is.
 *   - `lib/experiments.ts` (ADR-0034) — the 2-arm, reading_id-scoped loop whose
 *     readout is the BigQuery mart `v_experiment_readout` (z-test + SRM), joined
 *     to purchases via vedic_audit_log reading_id. Its A/A guardrail
 *     (`exposure_pipeline_v1`) stays on that loop untouched.
 *   - THIS module is the generalized WS-F rail: N weighted arms, browser
 *     (distinct_id) unit, session-deduped exposure, arm→Razorpay-notes server
 *     truth (works for ANY checkout, no reading_id needed), and a free DuckDB
 *     lake readout. Both emit the ONE `experiment_exposed` event; the ADR-0034
 *     BQ view filters `variant IN ('control','variant') AND reading_id IS NOT
 *     NULL`, so multi-arm rows never pollute its z-test/SRM. New multi-arm /
 *     browser-scoped experiments go in `experiments-config.ts`; converge the
 *     ADR-0034 registry into it when `exposure_pipeline_v1` concludes.
 *
 * Rule-47 note: `trackExperimentExposure` is idempotent within a session (string
 * key + sessionStorage), so even an unstable-effect caller cannot re-fire the
 * exposure. Still call it from the divergence point, not a render loop.
 */

import { trackOwnedAnalyticsOnly } from "@/lib/analytics-events";
import { getOrCreateBootstrapDistinctId } from "@/lib/analytics-identity";
import { safeSessionGet, safeSessionSet } from "@/lib/browser-storage";
import {
  EXPERIMENTS,
  experimentConfig,
  activeExperiments,
  type ExperimentArm,
  type ExperimentConfig,
} from "@/lib/experiments-config";

export type { ExperimentArm, ExperimentConfig };

const EXPERIMENT_BUSINESS_TIMEZONE = "Asia/Kolkata";

function businessDateInIndia(now: Date): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: EXPERIMENT_BUSINESS_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(now);
  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${values.year}-${values.month}-${values.day}`;
}

/** Active assignment cannot begin before its governed first IST business day. */
export function isExperimentStartEligible(config: ExperimentConfig, now = new Date()): boolean {
  return !config.startDate || businessDateInIndia(now) >= config.startDate;
}

/**
 * 32-bit FNV-1a + murmur3 avalanche finalizer (fmix32) — tiny, dependency-free.
 * Bare FNV-1a is measurably biased on structured/sequential keys (a 10k-id
 * chi-square pin caught chi2≈57 on 2 df); the finalizer restores avalanche so
 * the [0,1) fraction is uniform even for low-entropy client ids.
 */
function hash32(input: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  hash ^= hash >>> 16;
  hash = Math.imul(hash, 0x85ebca6b);
  hash ^= hash >>> 13;
  hash = Math.imul(hash, 0xc2b2ae35);
  hash ^= hash >>> 16;
  return hash >>> 0;
}

/** Whether this client is inside an experiment's bounded traffic allocation. */
export function isExperimentParticipant(config: ExperimentConfig, clientId: string): boolean {
  const allocation = config.pilot?.trafficAllocation ?? 1;
  if (allocation <= 0) return false;
  if (allocation >= 1) return true;
  const fraction = hash32(`${config.id}:allocation:${clientId}`) / 0x1_0000_0000;
  return fraction < allocation;
}

/**
 * Deterministic weighted arm assignment. Pure: the same
 * (experimentId, clientId) ALWAYS maps to the same arm — sticky by
 * construction, no storage writes. Weights are normalized, so
 * [{a,1},{b,1}] and [{a,50},{b,50}] are equivalent.
 */
export function assignArm(
  experimentId: string,
  arms: readonly ExperimentArm[],
  clientId: string
): string {
  if (arms.length === 0) return "";
  const total = arms.reduce((sum, arm) => sum + Math.max(0, arm.weight), 0);
  if (total <= 0) return arms[0].id;
  const fraction = hash32(`${experimentId}:${clientId}`) / 0x1_0000_0000;
  let cumulative = 0;
  for (const arm of arms) {
    cumulative += Math.max(0, arm.weight) / total;
    if (fraction < cumulative) return arm.id;
  }
  return arms[arms.length - 1].id; // float-rounding edge
}

function eligibleActiveConfig(
  experimentId: string,
  registry: readonly ExperimentConfig[]
): ExperimentConfig | null {
  const config = experimentConfig(experimentId, registry);
  return config?.status === "active" && isExperimentStartEligible(config) ? config : null;
}

function participantAssignment(config: ExperimentConfig): { clientId: string; arm: string } | null {
  const clientId = getOrCreateBootstrapDistinctId();
  if (!clientId || !isExperimentParticipant(config, clientId)) return null;
  return { clientId, arm: assignArm(config.id, config.arms, clientId) };
}

/**
 * The assigned arm for an ACTIVE experiment, or null when the experiment is
 * off/unknown or no browser identity exists (SSR). Callers branch behavior on
 * this WITHOUT emitting exposure — use `trackExperimentExposure` at the actual
 * divergence point.
 */
export function getExperimentArm(
  experimentId: string,
  registry: readonly ExperimentConfig[] = EXPERIMENTS
): string | null {
  const config = eligibleActiveConfig(experimentId, registry);
  return config ? (participantAssignment(config)?.arm ?? null) : null;
}

const EXPOSURE_SESSION_KEY_PREFIX = "ishvaram_exp_exposed:";
// In-memory mirror so storage-denied contexts (Safari private mode) still
// dedupe within the page lifetime, mirroring analytics-identity's fallback.
const exposedThisSession = new Set<string>();

/** Test-only: clear the in-memory exposure dedupe between test cases. */
export function resetExperimentExposuresForTests(): void {
  exposedThisSession.clear();
}

/** The assigned arm's normalized weight — the SRM check's expected share. */
function expectedShare(arms: readonly ExperimentArm[], armId: string): number {
  const total = arms.reduce((sum, arm) => sum + Math.max(0, arm.weight), 0);
  if (total <= 0) return 0;
  const arm = arms.find((candidate) => candidate.id === armId);
  return arm ? Math.max(0, arm.weight) / total : 0;
}

/**
 * Assign + emit the `experiment_exposed` owned-beacon event — ONCE per session
 * per experiment. Call this ONLY at the decision point where the arm's behavior
 * actually diverges (see the experiment's `exposurePoint` in the registry), and
 * branch on the returned arm. Returns null when the experiment is off/unknown
 * (no event, no behavior change) so an OFF experiment is fully inert.
 *
 * Payload mirrors the ADR-0034 shape (`variant`, `experiment_unit`) plus
 * `expected_share` — the assigned arm's normalized weight — so the lake
 * readout's SRM check reads its expected split from the emitting source
 * (the `expected_control_share` pattern generalized to N arms).
 */
export function trackExperimentExposure(
  experimentId: string,
  registry: readonly ExperimentConfig[] = EXPERIMENTS
): string | null {
  const config = eligibleActiveConfig(experimentId, registry);
  if (!config) return null;
  const assignment = participantAssignment(config);
  if (!assignment) return null;
  const { arm, clientId } = assignment;

  const key = `${EXPOSURE_SESSION_KEY_PREFIX}${experimentId}:${config.assignmentPolicy.assignmentVersion}`;
  const alreadyExposed = exposedThisSession.has(key) || safeSessionGet(key) !== null;
  if (!alreadyExposed) {
    exposedThisSession.add(key);
    safeSessionSet(key, arm);
    trackOwnedAnalyticsOnly("experiment_exposed", {
      experiment_id: experimentId,
      variant: arm,
      experiment_unit: "distinct_id",
      expected_share: expectedShare(config.arms, arm),
      traffic_allocation: config.pilot?.trafficAllocation ?? 1,
      assignment_version: config.assignmentPolicy.assignmentVersion,
      analysis_start_date: config.assignmentPolicy.analysisStartDate,
      client_id: clientId,
    });
  }
  return arm;
}

/**
 * The server-truth attribution payload: every ACTIVE experiment's assigned arm
 * as a compact `"expId:arm,expId2:arm2"` string ("" when none — the field is
 * then skipped by the backend notes compactor, so the rail costs zero note
 * slots until an experiment is live). Deterministic assignment guarantees this
 * always matches what `trackExperimentExposure` reported for the same browser.
 * Stamped by `createPaymentOrder` (lib/api/plans.ts) into Razorpay notes.
 */
export function experimentsNoteValue(registry: readonly ExperimentConfig[] = EXPERIMENTS): string {
  if (typeof window === "undefined") return "";
  const clientId = getOrCreateBootstrapDistinctId();
  if (!clientId) return "";
  return activeExperiments(registry)
    .filter((config) => isExperimentStartEligible(config))
    .filter((config) => isExperimentParticipant(config, clientId))
    .map((config) => `${config.id}:${assignArm(config.id, config.arms, clientId)}`)
    .join(",");
}
